import type { SupabaseClient } from '@supabase/supabase-js';

/** Private bucket for material files; downloads need a signed URL. */
export const MATERIALS_BUCKET = 'event-materials';

// How long a signed link stays valid. The page re-signs on every load and at
// each availability edge, so this only bounds how long a copied link works.
const SIGNED_URL_TTL_SECONDS = 60 * 60;

/**
 * Storage path inside the materials bucket for a stored file_url, or null when
 * the file lives elsewhere (another bucket, an external host). Accepts the
 * public, signed and authenticated object URL forms.
 */
export function materialStoragePath(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(new RegExp(`/storage/v1/object/(?:public|sign|authenticated)/${MATERIALS_BUCKET}/([^?#]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Signed, short-lived URLs for material files, created with the caller's own
 * session: the storage policy (can_read_material_object) decides per file, so
 * a participant whose ticket, payment or window does not allow the material
 * gets no URL. Files outside the materials bucket are returned unchanged.
 * Returns a map from the stored file_url to the URL to use.
 */
export async function signMaterialFileUrls(
  supabase: SupabaseClient,
  fileUrls: ReadonlyArray<string | null | undefined>,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  const toSign = new Map<string, string>(); // path -> stored url
  for (const url of fileUrls) {
    if (!url) continue;
    const path = materialStoragePath(url);
    if (path) toSign.set(path, url);
    else result.set(url, url);
  }
  if (toSign.size === 0) return result;

  const { data, error } = await supabase.storage
    .from(MATERIALS_BUCKET)
    .createSignedUrls([...toSign.keys()], SIGNED_URL_TTL_SECONDS);
  if (error) {
    console.error('Failed to sign material files:', error);
    return result;
  }
  for (const item of data ?? []) {
    const stored = item.path ? toSign.get(item.path) : undefined;
    if (stored && item.signedUrl) result.set(stored, item.signedUrl);
  }
  return result;
}

/** The same signed URL, asking the browser to save the file instead of opening it. */
export function asDownloadUrl(signedUrl: string): string {
  return signedUrl.includes('?') ? `${signedUrl}&download=` : `${signedUrl}?download=`;
}

/** Rows with their file_url swapped for a signed URL (null if signing was refused). */
export async function withSignedFiles<T extends { file_url: string | null }>(
  supabase: SupabaseClient,
  rows: T[],
): Promise<T[]> {
  const signed = await signMaterialFileUrls(supabase, rows.map((r) => r.file_url));
  return rows.map((r) => (r.file_url ? { ...r, file_url: signed.get(r.file_url) ?? null } : r));
}
